/**
 * Chunk Load Error Handler
 *
 * Handles automatic reload for ChunkLoadError (code splitting failures)
 * Common causes: outdated cached chunks after deployment, network issues
 *
 * Strategy: Reload once per session to recover, show fallback if still failing
 */

const AUTO_RELOAD_KEY = '__chunkload_auto_reload__';

/**
 * Detects if an error is a ChunkLoadError
 *
 * Checks for:
 * - error.name === "ChunkLoadError"
 * - error.message contains "ChunkLoadError" or "Loading chunk"
 * - error.message contains "Failed to fetch dynamically imported module"
 */
export function isChunkLoadError(error: Error): boolean {
  if (!error) return false;

  const errorName = error.name || '';
  const errorMessage = error.message || '';

  return (
    errorName === 'ChunkLoadError' ||
    errorMessage.includes('ChunkLoadError') ||
    errorMessage.includes('Loading chunk') ||
    errorMessage.includes('Failed to fetch dynamically imported module') ||
    errorMessage.includes('Importing a module script failed')
  );
}

/**
 * Attempts to auto-reload once per session
 *
 * Uses sessionStorage to prevent infinite reload loops
 *
 * @returns true if reload will happen, false if already reloaded this session
 */
export function autoReloadOnce(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const hasReloaded = sessionStorage.getItem(AUTO_RELOAD_KEY);

    if (!hasReloaded) {
      // Mark that we're about to reload
      sessionStorage.setItem(AUTO_RELOAD_KEY, Date.now().toString());

      // Hard reload to fetch fresh chunks
      window.location.reload();

      return true;
    }

    return false;
  } catch (error) {
    // SessionStorage might be unavailable (private browsing, etc)
    console.error('Failed to check/set auto-reload guard:', error);
    return false;
  }
}

/**
 * Checks if auto-reload has already happened this session
 */
export function hasAutoReloadedThisSession(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return sessionStorage.getItem(AUTO_RELOAD_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Gets build ID from Next.js data (for error reporting)
 */
export function getBuildId(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    return (window as any).__NEXT_DATA__?.buildId;
  } catch {
    return undefined;
  }
}

/**
 * Gets network connection info (for error reporting)
 */
export function getNetworkInfo(): Record<string, any> {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {};
  }

  try {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;

    if (!connection) return {};

    return {
      effective_type: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      save_data: connection.saveData,
    };
  } catch {
    return {};
  }
}
